import React, { useEffect, useRef } from "react";
import { ClientAccessUser, User } from "@/types/User.tsx";
import { useQuery } from "@tanstack/react-query";
import { ApiResponse } from "@/types/Api.tsx";
import GenericApi from "@/api/genericApi.tsx";
import { ApiRoutes } from "@/config/routes/ApiRoutes.tsx";
import { ClientAccessQueryKey } from "@/config/constants/QueryKeys.tsx";
import { AddClientAccess, Loader } from "@/components";
import { isBitramp } from "@/utils/utils";

interface ClientAccessCardProps {
  client: ClientAccessUser;
  users: User[] | null;
  isFirst?: boolean;
  handleAddUserToAccessList: (clientId: number, userIds: number[]) => void;
}

const ClientAccessCard: React.FC<ClientAccessCardProps> = ({
  client,
  isFirst,
  handleAddUserToAccessList,
  users,
}) => {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (cardRef.current && !cardRef.current.contains(event.target as Node)) {
        setIsExpanded(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const route = `${ApiRoutes.CLIENT_ACCESS}/${client.id}`;

  const {
    data: userClients,
    isLoading,
    error,
  } = useQuery<ApiResponse<ClientAccessUser[]>>({
    queryFn: async () => GenericApi.get<ClientAccessUser[]>(route),
    queryKey: [ClientAccessQueryKey, client.id],
  });

  const fullName = `${client.first_name} ${client.last_name}`;
  const isFullNameEmpty = fullName?.trim()?.length === 0;

  const handleAddUserToAccess = (userIds: number[]) => {
    handleAddUserToAccessList(client.id, userIds);
  };

  if (isLoading) {
    return <Loader />;
  }

  if (error || userClients?.error) {
    return <div>Error: {error?.message || userClients?.error?.message}</div>;
  }

  return (
    <div className="flex flex-col w-full" ref={cardRef}>
      <div
        onClick={toggleExpand}
        className={`flex rounded-[0.625rem] ${
          isExpanded ? "border-[1px] rounded-b-[0rem]" : "border-[1px]"
        } w-full justify-between items-center py-3 px-10 ${
          isFirst ? "mt-5" : ""
        } ${isBitramp() ? "border-[#464646]" : " "}`}
      >
        <div className="flex justify-center items-center gap-3">
          <p
            className={`Text  semi-bold w-[15rem] max-w-[15rem] ${
              isBitramp() ? "text-[#9F9F9F]" : "dark-blue"
            }`}
          >
            {!isFullNameEmpty ? fullName : "N/A"}
          </p>
          <p
            className={` Text ${isBitramp() ? "text-[#9F9F9F]" : "dark-blue"}`}
          >
            {client.accessible_users_count ?? "N/A"} clients
          </p>
        </div>
        <span>
          {isExpanded ? (
            <svg
              className="cursor-pointer"
              width="11"
              height="10"
              viewBox="0 0 11 10"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M4.61867 0.643939C4.99574 -0.0594038 6.00427 -0.0594026 6.38133 0.64394L10.3397 8.02751C10.6969 8.69368 10.2143 9.5 9.45841 9.5L1.54159 9.5C0.785718 9.5 0.303111 8.69368 0.660255 8.02751L4.61867 0.643939Z"
                fill={`${isBitramp() ? "#EE33D9" : "#222B45"}`}
              />
            </svg>
          ) : (
            <svg
              className="cursor-pointer"
              width="11"
              height="10"
              viewBox="0 0 11 10"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M6.38133 9.35606C6.00426 10.0594 4.99573 10.0594 4.61867 9.35606L0.660253 1.97249C0.30311 1.30632 0.785717 0.500001 1.54159 0.500001L9.45841 0.500001C10.2143 0.500001 10.6969 1.30632 10.3397 1.97249L6.38133 9.35606Z"
                fill={`${isBitramp() ? "#EE33D9" : "#222B45"}`}
              />
            </svg>
          )}
        </span>
      </div>
      {isExpanded ? (
        <div
          className={`flex flex-col gap-2 px-5 border-l-[1px] border-r-[1px] border-b-[1px] rounded-b-[0.625rem] ${
            isBitramp() ? " !border-[#464646] bg-[#2b2b2b]" : "bg-white"
          }`}
        >
          <AddClientAccess
            users={users}
            members={userClients?.data ?? []}
            handleAddUserToAccess={handleAddUserToAccess}
            onClose={() => setIsExpanded(false)}
          />
        </div>
      ) : null}
    </div>
  );
};

export default ClientAccessCard;
